/**
 * G0 (Get Found tactical wire) — the pure "editing the declared target-role
 * list" decisions, kept framework-free (no React, no fetch) so the add /
 * remove / dedupe / trim logic is unit-testable standalone, the same house
 * idiom presencePrewarmGate.ts / stageMove.ts / onboardingStep.ts already
 * establish for this app's pure modules (the decision is pure + tested, the
 * component that calls saveTargetRoles is the thin, untested wire).
 *
 * Mirrors apps/web/src/pages/Presence.tsx's own commitRoles/addRole/removeRole
 * trio (that file's L608-635) exactly in spirit — dedupe + trim happen
 * client-side here too (defense in depth; the server's PUT /presence/
 * target-roles ALSO dedupes case-insensitively + trims, per lens A's recon of
 * presence.routes.ts:1123), so the chip list never flashes a visible
 * duplicate for the one round trip before a server response would have
 * caught it.
 *
 * Case-insensitive dedupe matches the server's own rule (lens A) — "Product
 * Manager" and "product manager" are the same declared role, not two chips.
 */

/** Trim a raw draft string down to a candidate role, or null when there is
 *  nothing real to add (empty / whitespace-only draft). Never invents a role
 *  from nothing. */
export function normalizeRoleDraft(draft: string): string | null {
  const trimmed = draft.trim().replace(/\s+/g, ' ');
  return trimmed.length > 0 ? trimmed : null;
}

/** Case-insensitive "is this role already declared" check — mirrors the
 *  server's own dedupe rule (PUT /presence/target-roles trims + dedupes
 *  case-insensitively, lens A's recon of presence.routes.ts:1123). */
export function roleAlreadyDeclared(roles: string[], candidate: string): boolean {
  const lower = candidate.toLowerCase();
  return roles.some((r) => r.toLowerCase() === lower);
}

/**
 * Add a role to the declared list. Returns the SAME array reference (not a
 * new one) when the draft is empty/whitespace-only or already declared
 * (case-insensitively) — the caller uses reference equality to skip a no-op
 * saveTargetRoles call, same "guard the no-op before calling into the pure
 * module" pattern stageMove.ts's own doc comment describes for its callers.
 */
export function addRoleToList(roles: string[], draft: string): string[] {
  const candidate = normalizeRoleDraft(draft);
  if (candidate === null) return roles;
  if (roleAlreadyDeclared(roles, candidate)) return roles;
  return [...roles, candidate];
}

/**
 * Remove a role from the declared list by exact value (the chip's own
 * label, not a re-typed draft — no normalization needed on this path).
 * Returns the SAME array reference when the role isn't present, so a caller
 * can skip the round trip.
 */
export function removeRoleFromList(roles: string[], role: string): string[] {
  if (!roles.includes(role)) return roles;
  return roles.filter((r) => r !== role);
}

/** Whether an add would be a genuine no-op (nothing to add, or a duplicate)
 *  — lets a caller disable the Add button / skip the network call without
 *  re-deriving the same logic addRoleToList already encodes. */
export function isRoleAddNoop(roles: string[], draft: string): boolean {
  const candidate = normalizeRoleDraft(draft);
  return candidate === null || roleAlreadyDeclared(roles, candidate);
}
